// externals
import {
    createCorrelationMiddleware as createSharedCorrelationMiddleware,
    getActionStatus,
    ACTION_STATUS
} from '@makemydeal/dr-shared-store';

// types
import type { Middleware } from 'redux';
import type { SalesCheckinRootState } from '../common/types/SalesCheckinRootState';

// selectors
import { getDealXgId } from '../activeDeal/selector';
import { getCommonOrgId, getDealerDetails } from '../dealership/selector';

/**
 * Sales-checkin-specific correlation middleware
 *
 * This is a thin wrapper around the shared correlation middleware from @makemydeal/dr-shared-store
 * that configures it with sales-checkin-specific business context (dealXgId, commonOrgId, dealerMMDId).
 *
 * When enabled, actions are enriched with:
 * - correlationId, actionType, timestamp, sessionId
 * - dealXgId, commonOrgId, dealerMMDId (business context)
 * - callDepth, rootCorrelationId, parentCorrelationId (hierarchy tracking)
 * - New Relic PageActions and SPA interactions
 *
 * When disabled, only HTTP correlation is maintained for API debugging.
 *
 * For implementation details, see: @makemydeal/dr-shared-store/middleware/correlationMiddleware
 */
const baseCorrelationMiddleware: Middleware<
    Record<string, never>,
    SalesCheckinRootState
> = createSharedCorrelationMiddleware<SalesCheckinRootState>({
    // Runtime feature toggle configuration (US1827639)
    // Server-controlled kill switch via feature toggles service
    enabled: (state) => {
        // Use optional chaining to access toggle directly, avoiding selector's precondition check
        // If not yet loaded (during bootstrap), default to true to capture bootstrap actions
        return (state.featureToggles as Record<string, boolean | undefined>)?.enableReduxTelemetry ?? true;
    },

    // Store identifier for telemetry
    storeId: 'sales-checkin-store',

    // Extract sales-checkin-specific business context from state
    getBusinessContext: (state: SalesCheckinRootState) => {
        const dealer = getDealerDetails(state);
        return {
            dealXgId: getDealXgId(state),
            commonOrgId: getCommonOrgId(state),
            dealerMMDId: dealer.dealerMMDId ?? ''
        };
    }
});

/**
 * Wrapper middleware that adds actionStatus to all actions before passing to base middleware.
 */
export const correlationMiddleware: Middleware<Record<string, never>, SalesCheckinRootState> = (store) => {
    const baseMiddleware = baseCorrelationMiddleware(store);

    return (next) => {
        const baseNext = baseMiddleware(next);

        return (action) => {
            let modifiedAction = action;

            // Add actionStatus to all actions (US1815744)
            /* istanbul ignore next - Edge cases: action.meta undefined or RSAA actions */
            if (action.type && !action.meta?.actionStatus) {
                modifiedAction = {
                    ...modifiedAction,
                    meta: {
                        ...(modifiedAction.meta || {}),
                        actionStatus: getActionStatus(action.type)
                    }
                };
            } else if (!action.type && !action.meta?.actionStatus) {
                // RSAA actions don't have action.type initially, but we still need to set actionStatus
                modifiedAction = {
                    ...modifiedAction,
                    meta: {
                        ...(modifiedAction.meta || {}),
                        actionStatus: ACTION_STATUS.ASYNC_ACCEPTED
                    }
                };
            }

            return baseNext(modifiedAction);
        };
    };
};

/**
 * Re-export dispatchWithTelemetry from shared library for convenience
 */
export { dispatchWithTelemetry } from '@makemydeal/dr-shared-store';
